import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const SUPER_ADMIN_EMAIL = "aaron@transportmate.co.uk";

interface SuperAdminAccess {
  isSuperAdmin: boolean;
  loading: boolean;
  userId: string | null;
  email: string | null;
  checkAccess: () => Promise<boolean>;
}

/**
 * Hook to check and enforce Super Admin access.
 * Used consistently across all /sa/* routes.
 */
export const useSuperAdminAccess = (options?: { 
  redirect?: boolean; 
  redirectPath?: string;
}): SuperAdminAccess => {
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const { redirect = true, redirectPath = "/dashboard" } = options || {};

  const checkAccess = useCallback(async (): Promise<boolean> => {
    try {
      // Use getSession() (local cache, no network call) instead of getUser()
      // to prevent flooding /user endpoint on iOS cold starts
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      
      if (!user) {
        setIsSuperAdmin(false);
        setUserId(null);
        setEmail(null);
        return false;
      }
      
      setUserId(user.id);
      setEmail(user.email || null);
      
      const isSuper = user.email === SUPER_ADMIN_EMAIL;
      setIsSuperAdmin(isSuper);
      
      return isSuper;
    } catch (error) {
      console.error("Error checking super admin access:", error);
      setIsSuperAdmin(false);
      return false;
    }
  }, []);

  useEffect(() => {
    const performCheck = async () => {
      setLoading(true);
      const hasAccess = await checkAccess();
      
      if (!hasAccess && redirect) {
        toast({
          title: "Access Denied",
          description: "You don't have permission to access the Super Admin area.",
          variant: "destructive",
        });
        navigate(redirectPath);
      }
      
      setLoading(false);
    };
    
    performCheck();
    
    // Re-check only on meaningful auth events — NOT on TOKEN_REFRESHED
    // which fires frequently and would flood /user on iOS cold starts
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "SIGNED_OUT") {
        performCheck();
      }
    });
    
    return () => subscription.unsubscribe();
  }, [checkAccess, navigate, redirect, redirectPath, toast]);

  return { isSuperAdmin, loading, userId, email, checkAccess };
};

/**
 * Utility function to check if an email is super admin.
 * For use outside of React components.
 */
export const isSuperAdminEmail = (email: string | null | undefined): boolean => {
  return email === SUPER_ADMIN_EMAIL;
};

/**
 * Get the super admin email constant.
 */
export const getSuperAdminEmail = (): string => SUPER_ADMIN_EMAIL;
